import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { config } from "../../config";

interface User {
  userId: string;
  email: string;
  emailVerified: boolean;
  name?: string;
  status: string;
  enabled: boolean;
  createdAt: string;
  lastModified: string;
}

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [nextToken, setNextToken] = useState<string | null>(null);

  const fetchUsers = useCallback(
    async (token?: string) => {
      try {
        setIsLoading(true);
        setError(null);

        const params = new URLSearchParams();
        if (searchQuery) params.set("search", searchQuery);
        if (token) params.set("token", token);
        params.set("limit", "25");

        const response = await fetch(`${config.apiBaseUrl}/v1/admin/users?${params.toString()}`, {
          credentials: "include",
        });

        if (!response.ok) throw new Error("Failed to fetch users");

        const data = await response.json();

        if (token) {
          setUsers((prev) => [...prev, ...(data.users ?? [])]);
        } else {
          setUsers(data.users ?? []);
        }
        setNextToken(data.nextToken ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setIsLoading(false);
      }
    },
    [searchQuery],
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setNextToken(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Users</h1>
      </header>

      <div className="admin-toolbar">
        <div className="search-group">
          <input
            type="text"
            placeholder="Search by email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="search-input"
          />
          <button type="button" className="btn btn-primary" onClick={handleSearch}>
            Search
          </button>
        </div>
      </div>

      {isLoading && users.length === 0 && (
        <div className="admin-loading">
          <div className="loading-spinner" />
          <p>Loading users...</p>
        </div>
      )}

      {error && (
        <div className="admin-error">
          <p>{error}</p>
          <button type="button" onClick={() => fetchUsers()}>
            Retry
          </button>
        </div>
      )}

      {!isLoading && !error && users.length === 0 && (
        <div className="admin-empty">
          <p>No users found.</p>
        </div>
      )}

      {users.length > 0 && (
        <>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Status</th>
                <th>Enabled</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.userId}>
                  <td>
                    <Link to={`/admin/users/${user.userId}`}>{user.email}</Link>
                  </td>
                  <td>{user.name ?? "-"}</td>
                  <td>
                    <span className={`badge ${user.status === "CONFIRMED" ? "badge-success" : ""}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>
                    {user.enabled ? (
                      <span className="badge badge-success">Yes</span>
                    ) : (
                      <span className="badge badge-danger">No</span>
                    )}
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    <Link to={`/admin/users/${user.userId}`} className="btn-link">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {nextToken && (
            <div className="pagination-controls">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => fetchUsers(nextToken)}
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
